import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Star,
  MapPin,
  Trophy,
  Users,
  LogOut,
  Target,
  ListOrdered,
  Medal,
  Award,
  UploadCloud,
  Map,
  UserCog,
  Printer,
  Unlock,
  ShieldAlert,
  Calculator,
  HelpCircle,
  Tablet,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function SidebarHeader() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-yellow-400 flex items-center justify-center">
        <img
          src="/logo192.png"
          alt="Kirtagames Logo"
          className="w-10 h-10 rounded-xl object-contain"
        />
      </div>
      <div className="leading-tight">
        <div className="text-sm text-yellow-300">Kirtagames</div>
      </div>
    </div>
  );
}

function NavItem({ to, icon, label, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center h-10 gap-3 mb-1 px-3 rounded-lg transition-all duration-200 text-gray-300 hover:bg-gray-700 hover:text-yellow-300"
    >
      <span className="flex items-center justify-center w-6">{icon}</span>
      <span className="flex-1">{label}</span>
    </Link>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="px-3 mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 first:mt-0">
      {children}
    </div>
  );
}

function SidebarFooter({ username, role, onLogout }) {
  return (
    <div className="flex items-center gap-3 border-t border-gray-800 pt-4">
      <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-white">
        {username ? username.charAt(0).toUpperCase() : "U"}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">{username || "Unbekannt"}</div>
        <div className="text-xs text-yellow-300">{role}</div>
      </div>
      <button
        onClick={onLogout}
        className="px-2 py-1 rounded hover:bg-gray-800"
        aria-label="Logout"
      >
        <LogOut size={16} />
      </button>
    </div>
  );
}

export default function Sidebar({ user, open, onClose }) {
  const { logout } = useAuth();
  const [publicEnabled, setPublicEnabled] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Prüfe, ob Ergebnisse öffentlich sind
  useEffect(() => {
    const loadState = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/public/state`);
        const data = await res.json();
        setPublicEnabled(!!data.results_published);
      } catch (err) {
        console.warn("Konnte Freigabe-Status nicht laden:", err);
      }
    };
    loadState();
  }, [BACKEND_URL]);

  const handleLogout = () => {
    logout();
    if (onClose) onClose();
  };

  // Die Seitenleiste gibt es nur mit Anmeldung (App.js). Frueher war hier
  // zusaetzlich eine Ansicht fuer nicht angemeldete Besucher vorgesehen
  // (Rolle "public"), die nie erreicht wurde.
  if (!user) {
    return null;
  }

  // Navigation nach Themen gruppiert, statt einer flachen Liste. Innerhalb
  // jeder Gruppe wird nur angezeigt, was zur Rolle passt; eine Gruppe ohne
  // sichtbare Eintraege faellt komplett weg (kein leerer Abschnitts-Titel).
  const navGroups = [
    {
      title: "Teams",
      items: [
        { key: "teams", label: "Teams", to: "/teams", icon: <Users size={18} />, roles: ["admin"] },
        { key: "import", label: "Teams importieren", to: "/import", icon: <UploadCloud size={18} />, roles: ["admin"] },
      ],
    },
    {
      title: "Wettkampf",
      items: [
        { key: "station", label: "Stationen", to: "/station", icon: <MapPin size={18} />, roles: ["admin", "betreuer"] },
        { key: "import-stationen", label: "Stationen importieren", to: "/import-stationen", icon: <UploadCloud size={18} />, roles: ["admin"] },
        { key: "quiz", label: "Quiz", to: "/quiz-verwaltung", icon: <HelpCircle size={18} />, roles: ["admin"] },
        { key: "quiz-tablet", label: "Quiz-Tablet testen", to: "/quiz", icon: <Tablet size={18} />, roles: ["admin"] },
        { key: "wertung", label: "Wertung", to: "/wertung", icon: <Calculator size={18} />, roles: ["admin"] },
        { key: "agebonus", label: "Altersbonus", to: "/agebonus", icon: <Star size={18} />, roles: ["admin"] },
        { key: "lageplan", label: "Lageplan", to: "/lageplan", icon: <Map size={18} />, roles: ["admin"] },
      ],
    },
    {
      title: "Ergebnisse",
      items: [
        { key: "ergebnisse", label: "Ergebnisse", to: "/ergebnisse", icon: <Trophy size={18} />, roles: ["admin"], nachFreigabe: true },
        { key: "liveergebnisse", label: "Live-Ergebnisse", to: "/liveergebnisse", icon: <Target size={18} />, roles: ["admin"] },
      ],
    },
    {
      title: "Preisverleihung",
      items: [
        { key: "preisverleihung", label: "Preisverleihung", to: "/preisverleihung", icon: <Medal size={18} />, roles: ["admin"] },
        { key: "preise-verwalten", label: "Preise verwalten", to: "/preise-verwalten", icon: <Award size={18} />, roles: ["admin"] },
      ],
    },
    {
      title: "Drucke & Exporte",
      items: [
        { key: "exporte", label: "Drucke & Exporte", to: "/exporte", icon: <Printer size={18} />, roles: ["admin"] },
      ],
    },
    {
      title: "Admin",
      items: [
        { key: "freischalten", label: "Freischalten", to: "/freischalten", icon: <Unlock size={18} />, roles: ["admin"] },
        { key: "nutzer", label: "Nutzerverwaltung", to: "/nutzer", icon: <UserCog size={18} />, roles: ["admin"] },
        { key: "systemverwaltung", label: "Systemverwaltung", to: "/systemverwaltung", icon: <ShieldAlert size={18} />, roles: ["admin"] },
      ],
    },
  ];

  const role = user.role;
  const username = user.username;

  const renderNav = (onItemClick) => (
    <>
      {/* Frei zugängliches Ranking */}
      <NavItem to="/ranking" icon={<ListOrdered size={18} />} label="Ranking" onClick={onItemClick} />
      {navGroups.map((group) => {
        // `nachFreigabe`: nach der Ergebnis-Freigabe fuer alle Angemeldeten –
        // Betreuer sehen unter /ergebnisse dann die oeffentliche Ansicht.
        const sichtbareItems = group.items.filter(
          (item) => item.roles.includes(role) || (item.nachFreigabe && publicEnabled)
        );
        if (sichtbareItems.length === 0) return null;
        return (
          <div key={group.title}>
            <SectionLabel>{group.title}</SectionLabel>
            {sichtbareItems.map((item) => (
              <NavItem
                key={item.key}
                to={item.to}
                icon={item.icon}
                label={item.label}
                onClick={onItemClick}
              />
            ))}
          </div>
        );
      })}
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 lg:w-72 bg-gray-900 text-white p-4 h-screen sticky top-0 z-20 rounded-r-xl shadow-lg">
        <SidebarHeader />
        <nav className="mt-6 flex-1 overflow-y-auto">{renderNav()}</nav>
        {user && <SidebarFooter username={username} role={role} onLogout={handleLogout} />}
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              aria-hidden="true"
            />

            <motion.aside
              className="fixed left-0 top-0 bottom-0 w-72 bg-gray-900 text-white p-4 z-50 shadow-2xl rounded-r-xl overflow-y-auto"
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-center justify-between">
                <SidebarHeader />
                <button
                  aria-label="Schließen"
                  onClick={onClose}
                  className="p-2"
                >
                  <X size={20} />
                </button>
              </div>

              <nav className="mt-6">{renderNav(onClose)}</nav>

              {user && (
                <div className="mt-auto pt-4">
                  <SidebarFooter username={username} role={role} onLogout={handleLogout} />
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
